import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

// Cache the result to avoid repeated calls
let cachedResult: {
  isComingSoonEnabled: boolean;
  countdownDate: string;
  timestamp: number;
} | null = null;

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useComingSoonStatus() {
  const [isComingSoonEnabled, setIsComingSoonEnabled] = useState(false);
  const [countdownDate, setCountdownDate] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkComingSoonStatus();
  }, []);

  const checkComingSoonStatus = async () => {
    try {
      // Check cache first
      const now = Date.now();
      if (cachedResult && now - cachedResult.timestamp < CACHE_DURATION) {
        console.log("Using cached coming soon status");
        setIsComingSoonEnabled(cachedResult.isComingSoonEnabled);
        setCountdownDate(cachedResult.countdownDate);
        setIsLoading(false);
        return;
      }

      console.log("Checking coming soon status...");

      // Use Promise.all for better performance
      const [comingSoonResult, countdownResult] = await Promise.all([
        supabase.rpc("get_app_setting", { setting_key: "coming_soon_enabled" }),
        supabase.rpc("get_app_text_setting", {
          setting_key: "countdown_target_date",
        }),
      ]);

      const isEnabled = comingSoonResult.data || false;
      const countdown = countdownResult.data || "";

      // Cache the result
      cachedResult = {
        isComingSoonEnabled: isEnabled,
        countdownDate: countdown,
        timestamp: now,
      };

      if (comingSoonResult.error) {
        console.error(
          "Error checking coming soon status:",
          comingSoonResult.error,
        );
        setIsComingSoonEnabled(false);
      } else {
        console.log("Coming soon status:", isEnabled);
        setIsComingSoonEnabled(isEnabled);
      }

      if (countdownResult.error) {
        console.error("Error checking countdown date:", countdownResult.error);
        setCountdownDate("");
      } else {
        console.log("Countdown date:", countdown);
        setCountdownDate(countdown);
      }
    } catch (error) {
      console.error("Failed to check coming soon status:", error);
      setIsComingSoonEnabled(false);
      setCountdownDate("");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isComingSoonEnabled,
    countdownDate,
    isLoading,
    refetch: checkComingSoonStatus,
  };
}
